import moment from 'moment'
import { FINANCE_VS } from '@constants/Dashboard/financeDashboard.constants'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type {
  DashboardPeriod,
  HygieneProductionPeriodBucket,
  PeriodComparisonMetric,
} from '@services/api/dashboard/finance.api'

export const RANGE_TO_PERIOD: Record<RangeKey, DashboardPeriod> = {
  '1D': 'day',
  '1W': 'week',
  '1M': 'month',
  '1Y': 'year',
  YTD: 'ytd',
}

export const CALENDAR_DATE_FORMAT = 'MMMM D, YYYY'

export function rangeToPeriod(range: RangeKey): DashboardPeriod {
  return RANGE_TO_PERIOD[range]
}

export function calendarDateToIso(date: string): string {
  const parsed = moment(date, CALENDAR_DATE_FORMAT, true)
  return (parsed.isValid() ? parsed : moment()).toISOString()
}

function formatCurrencyFull(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatChartCompactSuffix(
  value: number,
  divisor: number,
  suffix: 'K' | 'M' | 'B',
): string {
  const scaled = value / divisor
  const scaledStr = scaled.toFixed(2).replace(/\.?0+$/, '')
  return `$${scaledStr}${suffix}`
}

/** Chart axis/tooltip: full $ below 1k; $K / $M / $B with max 2 decimal places at/above 1k. */
export function formatChartCurrencyDollars(dollars: number): string {
  if (!Number.isFinite(dollars)) return '—'
  if (dollars === 0) return '$0'
  const abs = Math.abs(dollars)
  if (abs < 1000) return formatCurrencyFull(dollars)
  if (abs >= 1_000_000_000) {
    return formatChartCompactSuffix(dollars, 1_000_000_000, 'B')
  }
  if (abs >= 1_000_000) return formatChartCompactSuffix(dollars, 1_000_000, 'M')
  return formatChartCompactSuffix(dollars, 1000, 'K')
}

export function formatCurrency(
  value: number,
  options?: { compact?: boolean },
): string {
  if (!Number.isFinite(value)) return '—'
  if (options?.compact && Math.abs(value) >= 1000) {
    return formatChartCurrencyDollars(value)
  }
  return formatCurrencyFull(value)
}

/** Chart values stored as thousands of dollars (e.g. production / 1000). */
export function formatChartCurrencyKUnits(kUnits: number): string {
  if (!Number.isFinite(kUnits)) return '—'
  return formatChartCurrencyDollars(kUnits * 1000)
}

/** @deprecated Prefer `formatChartCurrencyKUnits`. */
export function formatProductionThousands(value: number): string {
  return formatChartCurrencyKUnits(value)
}

export function formatPercent(value: number, digits = 1): string {
  if (!Number.isFinite(value)) return '—'
  return `${value.toFixed(digits)}%`
}

export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return '—'
  return value.toLocaleString('en-US')
}

export function financeTrendLine(
  range: RangeKey,
  signedPct: string,
  amt: string,
): string {
  return `${signedPct} (${amt}) ${FINANCE_VS[range]}`
}

/** Short label for bar chart X-axis (e.g. `Dr. Jack Monroe` → `J. Monroe`). */
export function financeProviderChartLabel(providerName: string): string {
  const stripped = providerName.replace(/^Dr\.\s*/i, '').trim()
  const parts = stripped.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}. ${parts[parts.length - 1]}`
  }
  return providerName
}

export type ComparisonFooter = {
  up: boolean
  text: string
}

export function buildComparisonFooter(
  metric: PeriodComparisonMetric,
  options?: { isCurrency?: boolean; isCount?: boolean },
): ComparisonFooter {
  const { percentChange, previous } = metric
  const up = percentChange >= 0
  const pct = Math.abs(percentChange)
  let previousText: string
  if (options?.isCount) {
    previousText = formatCount(previous)
  } else {
    previousText = formatCurrency(previous)
  }
  const sign = pct > 0 ? `${pct.toFixed(1)}%` : '0%'
  return {
    up,
    text: `${sign} (${previousText}) vs prior period`,
  }
}

export type ChartPoint = { label: string; value: number; isCurrent?: boolean }

export type ChartAxisConfig = {
  yDomain: [number, number]
  yTicks: number[]
}

function niceTickStep(roughStep: number): number {
  if (!Number.isFinite(roughStep) || roughStep <= 0) return 1
  const exp = Math.floor(Math.log10(roughStep))
  const base = 10 ** exp
  const f = roughStep / base
  const niceF = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10
  return niceF * base
}

export function computeChartAxis(
  values: number[],
  segmentCount = 4,
): ChartAxisConfig {
  const maxDataValue = values.length ? Math.max(...values, 0) : 0
  if (maxDataValue <= 0) {
    return { yDomain: [0, 1], yTicks: [0, 0.25, 0.5, 0.75, 1] }
  }
  const targetMax = maxDataValue * 1.08
  const roughStep = targetMax / segmentCount
  const step = niceTickStep(roughStep)
  const axisMax = Math.max(step, Math.ceil(targetMax / step) * step)
  const yTicks: number[] = []
  const maxSteps = Math.ceil(axisMax / step) + 1
  for (let i = 0; i <= maxSteps; i++) {
    const v = Math.round(step * i * 100) / 100
    if (v > axisMax + step * 0.01) break
    yTicks.push(v)
  }
  if (yTicks.length < 2) yTicks.push(axisMax)
  return { yDomain: [0, axisMax], yTicks }
}

/** Parse month index (0–11) from a period/chart label; null when unparseable. */
export function parsePeriodLabelMonth(
  label: string,
  referenceYear?: number,
): number | null {
  const cleaned = label.replace(/\s*\([^)]*\)\s*$/, '').trim()
  if (!cleaned) return null

  const withYear = moment(
    cleaned,
    ['MMM YYYY', 'MMMM YYYY', 'MMM YY', 'MMMM YY'],
    true,
  )
  if (withYear.isValid()) return withYear.month()

  const monthOnly = moment(cleaned, ['MMM', 'MMMM'], true)
  if (monthOnly.isValid()) {
    if (referenceYear !== undefined) {
      monthOnly.year(referenceYear)
    }
    return monthOnly.month()
  }

  return null
}

/** Cap YTD period buckets to months through the selected dashboard date. */
export function periodBucketDollars(bucket: {
  value?: number
  production?: number
}): number {
  const amount = bucket.value ?? bucket.production
  return Number.isFinite(amount) ? amount! : 0
}

export function filterYtdPeriodItems<T>(
  range: RangeKey,
  date: string,
  items: T[],
  getLabel: (item: T) => string,
): T[] {
  if (range !== 'YTD' || items.length === 0) return items

  const selected = moment(date, CALENDAR_DATE_FORMAT, true)
  if (!selected.isValid()) return items

  const endMonth = selected.month()
  const referenceYear = selected.year()

  const filtered = items.filter((item) => {
    const month = parsePeriodLabelMonth(getLabel(item), referenceYear)
    if (month === null) return true
    return month <= endMonth
  })

  const anyParsed = items.some(
    (item) => parsePeriodLabelMonth(getLabel(item), referenceYear) !== null,
  )
  if (!anyParsed && items.length === 12) {
    return items.slice(0, endMonth + 1)
  }

  return filtered
}

export function detectPeriodSummaryThousandsScale(
  rawValues: number[],
  referenceTotalDollars?: number,
): 'dollars' | 'thousands' {
  const max = rawValues.length ? Math.max(...rawValues, 0) : 0
  if (max <= 0) return 'dollars'
  if (max >= 10_000) return 'dollars'

  if (
    referenceTotalDollars &&
    referenceTotalDollars >= 10_000 &&
    max <= referenceTotalDollars / 50
  ) {
    return 'thousands'
  }

  if (max >= 1_000) return 'dollars'
  return 'thousands'
}

export function periodSummaryToChartConfig(
  buckets: HygieneProductionPeriodBucket[],
  options?: {
    valueInThousands?: boolean
    /** Hygiene production total in dollars — helps detect live vs mock bucket units. */
    referenceTotalDollars?: number
    markLastAsCurrent?: boolean
    range?: RangeKey
    date?: string
  },
): { data: ChartPoint[] } & ChartAxisConfig {
  const filtered =
    options?.range && options?.date
      ? filterYtdPeriodItems(
          options.range,
          options.date,
          buckets,
          (b) => b.label,
        )
      : buckets

  const rawValues = filtered.map((b) => periodBucketDollars(b))
  const thousandsScale =
    options?.valueInThousands === true
      ? detectPeriodSummaryThousandsScale(
          rawValues,
          options.referenceTotalDollars,
        )
      : null

  const data: ChartPoint[] = filtered.map((b, i) => {
    const raw = periodBucketDollars(b)
    let value = raw
    if (options?.valueInThousands) {
      value = thousandsScale === 'thousands' ? raw : raw / 1000
    }
    return {
      label: b.label,
      value,
      isCurrent:
        options?.markLastAsCurrent === true && i === filtered.length - 1,
    }
  })
  const axis = computeChartAxis(data.map((d) => d.value))
  return { data, ...axis }
}

const PROCEDURE_CHART_PALETTE = [
  'var(--blue-400)',
  'var(--indigo-400)',
  'var(--gray-200)',
  'var(--red-400)',
  'var(--pale-green-400)',
  'var(--yellow-400)',
  'var(--teal-400)',
]

export function categoryColorsFromLabels(
  labels: string[],
): Record<string, string> {
  const colors: Record<string, string> = {}
  labels.forEach((label, i) => {
    colors[label] = PROCEDURE_CHART_PALETTE[i % PROCEDURE_CHART_PALETTE.length]
  })
  return colors
}

export function formatHygieneVisitDate(iso: string): string {
  const m = moment(iso)
  return m.isValid() ? m.format('MMM D, YYYY') : iso
}

export function formatProcedureLogsServices(
  logs: { proc_description?: string }[] | undefined,
): string {
  if (!logs?.length) return '—'
  const descriptions = logs
    .map((l) => l.proc_description)
    .filter((d): d is string => Boolean(d))
  return descriptions.length ? descriptions.join('; ') : '—'
}

export function toGoalNumber(
  value: number | string | null | undefined,
): number {
  if (value == null) return 0
  const n = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(n) ? n : 0
}

export function formatGoalCurrencyLabel(
  amountDollars: number | string | null | undefined,
): string {
  const value = toGoalNumber(amountDollars)
  if (value === 0) return 'Goal $0'
  return `Goal ${formatChartCurrencyDollars(value)}`
}

/** Convert a dollar goal from the API to chart Y-axis units (thousands). */
export function goalDollarsToChartUnits(
  amountDollars: number | string | null | undefined,
): number {
  return toGoalNumber(amountDollars) / 1000
}

export function formatDaysToCollectGoal(
  days: number | string | null | undefined,
): string {
  const value = toGoalNumber(days)
  if (value <= 0) return 'Goal —'
  return `Goal < ${Math.round(value)} days`
}

export const HYGIENE_PLACEHOLDER = '—'
